import { useEffect, useState, useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { MonitoreoService } from "../../service/monitoreo/MonitoreoService";
import { Badge } from 'primereact/badge';
import { Card } from 'primereact/card';
import { OverlayPanel } from 'primereact/overlaypanel';
import { TabView, TabPanel } from 'primereact/tabview';

export const MapaCalorRiesgo = ({ matrizId, permss, toast, generic, graficoRef, defaultTab = 0, refrescar = 0 }) => {
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(defaultTab);
    const [paresRiesgoInherente, setParesRiesgoInherente] = useState([]);
    const [paresRiesgoResidual, setParesRiesgoResidual] = useState([]);
    const [siglasRiesgos, setSiglasRiesgos] = useState([]);
    const overlayRef = useRef(null);
    const matrizSrv = new MonitoreoService();
    const cellSize = 60;
    const labelSize = 60;

    const niveles = [
        { nombre: 'Muy Bajo', valor: 1, color: '#00b0f0' },
        { nombre: 'Bajo', valor: 2, color: '#00ff00' },
        { nombre: 'Medio', valor: 3, color: '#ffff00' },
        { nombre: 'Alto', valor: 4, color: '#ffc000' },
        { nombre: 'Muy Alto', valor: 5, color: '#ff0000' },
    ];

    const initialGrid = Array.from({ length: 5 }, () =>
        Array.from({ length: 5 }, () => ({ count: 0, siglas: [] }))
    );

    useEffect(() => {
        if (matrizId) {
            consultar();
        }
    }, [matrizId, refrescar]);

    const consultar = () => {
        setLoading(true);
        matrizSrv.consultar(`${matrizId}`, 'matriz-riesgo/pares-mapa-calor').then(res => {
            const datosTransformados = (res.data || []).map(d => ({
                ...d,
                impacto: parseInt(d.impacto, 10) || 0,
                probabilidad_p1: parseInt(d.probabilidad_p1, 10) || 0,
                consecuencias: parseInt(d.consecuencias, 10) || 0,
                probabilidad: Math.round(parseFloat(d.probabilidad) || 0),
                sigla_riesgo: d.sigla_riesgo || '',
            }));

            const inherente = datosTransformados.map(d => ({
                x: d.impacto,
                y: d.probabilidad_p1,
                sigla: d.sigla_riesgo
            }));
            setParesRiesgoInherente(inherente);

            const residual = datosTransformados.map(d => ({
                x: d.consecuencias,
                y: d.probabilidad,
                sigla: d.sigla_riesgo
            }));
            setParesRiesgoResidual(residual);
        }).catch(error => {
            generic.funcDeleteError(error, toast);
        }).finally(() => setLoading(false));
    };

    const contarCeldas = (pares) => {
        const grid = initialGrid.map(row => row.map(cell => ({ ...cell })));
        pares.forEach(({ x, y, sigla }) => {
            if (x >= 1 && x <= 5 && y >= 1 && y <= 5) {
                const rowIndex = 5 - y;
                const colIndex = x - 1;
                grid[rowIndex][colIndex].count += 1;
                if (sigla) {
                    if (!grid[rowIndex][colIndex].siglas.includes(sigla)) {
                        grid[rowIndex][colIndex].siglas.push(sigla);
                    }
                }
            }
        });
        return grid;
    };

    const getBaseColor = (x, y) => {
        const muyBajo = [[1, 1]];
        const bajo = [[2, 1], [3, 1], [1, 2], [2, 2], [1, 3]];
        const medio = [[4, 1], [5, 1], [3, 2], [4, 2], [2, 3], [3, 3], [1, 4], [2, 4], [1, 5]];
        const alto = [[5, 2], [4, 3], [5, 3], [3, 4], [4, 4], [2, 5], [3, 5]];
        if (muyBajo.some(([px, py]) => px === x && py === y)) return niveles[0].color;
        if (bajo.some(([px, py]) => px === x && py === y)) return niveles[1].color;
        if (medio.some(([px, py]) => px === x && py === y)) return niveles[2].color;
        if (alto.some(([px, py]) => px === x && py === y)) return niveles[3].color;
        return niveles[4].color;
    };

    const getRGBA = (hex, alpha) => {
        const bigint = parseInt(hex.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r},${g},${b},${alpha})`;
    };

    const gridValues = contarCeldas(activeIndex === 0 ? paresRiesgoInherente : paresRiesgoResidual);

    const handleMouseEnterCell = (event, cell) => {
        if (cell.count > 0) {
            setSiglasRiesgos(cell.siglas);
            if (overlayRef.current) overlayRef.current.show(event);
        }
    };

    const handleMouseLeaveCell = () => {
        if (overlayRef.current) overlayRef.current.hide();
    };

    const renderMapa = (gridValues) => {
        const titulo = activeIndex === 0 ? 'Riesgo Inherente' : 'Riesgo Residual (Con Controles)';
        return (
            <>
                <OverlayPanel ref={overlayRef} style={{ width: '250px' }}>
                    <div style={{ padding: '0.25rem 0.5rem' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.4rem', color: '#333' }}>
                            ID del riesgo
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))', gap: '6px' }}>
                            {siglasRiesgos.map(sigla => (
                                <div key={`sigla-${sigla}`}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: '#222',
                                        padding: '6px 10px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', fontWeight: 600,
                                        border: '1px solid rgba(0,0,0,0.4)', textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'anywhere',
                                    }}
                                >
                                    {sigla}
                                </div>
                            ))}
                        </div>
                    </div>
                </OverlayPanel>

                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '500px', color: '#444' }}>
                        <ProgressSpinner style={{ width: '60px', height: '60px' }} strokeWidth={5} />
                        <span style={{ marginTop: '1rem', fontSize: '1.1rem' }}>Cargando mapa de calor...</span>
                    </div>
                ) : (
                    <>
                        <div ref={graficoRef}>
                            <h4 style={{ marginBottom: '0.2rem', color: '#111', fontWeight: 'bold', textAlign: 'center' }}>
                                {titulo}
                            </h4>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0.5rem', width: '100%', height: '100%', }}>
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                                    {/* Leyenda Eje Y rotado */}
                                    <div style={{ display: 'inline-block', transform: 'rotate(-90deg)', transformOrigin: 'left top', fontWeight: 'bold', fontSize: '1.2rem', color: '#111', marginRight: '-55px' }}>
                                        Probabilidad
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: `${labelSize}px repeat(5, ${cellSize}px)`, gridTemplateRows: `repeat(5, ${cellSize}px) ${labelSize}px`, gap: '6px' }}>
                                            {/* Etiquetas Y */}
                                            {niveles.slice().reverse().map((nivel, i) => (
                                                <Card key={`labelY-${nivel.valor}`} style={{
                                                    gridColumn: '1 / 2', gridRow: `${i + 1} / ${i + 2}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontWeight: 'bold', fontSize: '0.85rem', backgroundColor: getRGBA(nivel.color, 0.8), borderRadius: '8px', textAlign: 'center', wordBreak: 'break-word',
                                                }} className="shadow-2">
                                                    <div style={{ color: '#000000' }}>{nivel.nombre}</div>
                                                    <div style={{ fontSize: '1.1rem', color: '#000000' }}>{nivel.valor}</div>
                                                </Card>
                                            ))}

                                            {/* Celdas */}
                                            {gridValues.map((row, rowIndex) => row.map((cell, colIndex) => {
                                                const x = colIndex + 1;
                                                const y = 5 - rowIndex;
                                                const baseColor = getBaseColor(x, y);
                                                const opacity = cell.count > 0 ? Math.min(0.5 + cell.count * 0.12, 0.95) : 0.25;
                                                return (
                                                    <Card key={`cell-${x}-${y}`} onMouseEnter={(e) => handleMouseEnterCell(e, cell)} onMouseLeave={handleMouseLeaveCell}
                                                        style={{
                                                            gridColumn: `${colIndex + 2} / ${colIndex + 3}`, gridRow: `${rowIndex + 1} / ${rowIndex + 2}`, width: `${cellSize}px`,
                                                            height: `${cellSize}px`, backgroundColor: getRGBA(baseColor, opacity), borderRadius: '12px', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1rem', cursor: cell.count > 0 ? 'pointer' : 'default',
                                                            transition: 'transform .12s',
                                                        }}
                                                        className={cell.count > 0 ? "shadow-4" : "shadow-1"}>
                                                        {cell.count > 0 && <div style={{ position: 'relative', width: 0.01 * cellSize, height: 0.01 * cellSize }}>
                                                            <Badge value={cell.count} severity="secondary" style={{
                                                                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                                                width: '24px', height: '24px', lineHeight: '24px', textAlign: 'center', borderRadius: '50%'
                                                            }} />
                                                        </div>}
                                                    </Card>
                                                );
                                            }))}
                                            {/* Etiquetas X */}
                                            {niveles.map((nivel, i) => (
                                                <Card key={`labelX-${nivel.valor}`} style={{
                                                    gridColumn: `${i + 2} / ${i + 3}`, gridRow: '6 / 7', display: 'flex', alignItems: 'center',
                                                    justifyContent: 'center', backgroundColor: getRGBA(nivel.color, 0.8), borderRadius: '8px', fontWeight: 'bold',
                                                    fontSize: '0.85rem', textAlign: 'center', wordBreak: 'break-word'
                                                }} className="shadow-2">
                                                    <div style={{ color: '#000000' }}>{nivel.nombre}</div>
                                                    <div style={{ fontSize: '1.1rem', color: '#000000' }}>{nivel.valor}</div>
                                                </Card>
                                            ))}
                                        </div>
                                        {/* Leyenda eje X */}
                                        <div style={{ marginTop: '0.6rem', fontSize: '1.1rem', color: '#111', textAlign: 'center', paddingLeft: '55px', width: '250px', }}>
                                            <strong>Consecuencia - Impacto</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}        >
                <TabPanel header="Riesgo Inherente">
                    {renderMapa(gridValues)}
                </TabPanel>
                <TabPanel header="Riesgo Residual">
                    {renderMapa(gridValues)}
                </TabPanel>
            </TabView>
        </>
    );
};